import { inject, observer } from 'mobx-react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Lifecycle } from '@byted-cg/linkchat-core';
import { AppContextType } from '@byted-cg/linkchat-plugin';
import { ErrorBlock, Popup } from '@byted-cg/linkchat-ui';

import classes from '../constants/classes';
import { LayoutEventBus } from '../constants/eventBus';
import { prefixCls } from '../utils/style';
import useClickOutside from '../utils/useClickOutSide';
import DynamicStyles from './DynamicStyles';
import EmojiPanel from './EmojiPanel';
import Ghost, { GhostRefs } from './Ghost';
import { LocalProvider } from './index';
import InputWrapper, { IInputWrapperRef } from './InputWrapper';
import LazyComponent from './LazyComponent';
import MessageWrapper from './MessageWrapper';
import Navbar from './Navbar';
import Optional, { IOptionalRef } from './Optional';
import PanelWrapper, { IPanelWrapperRef } from './PanelWrapper';
import PCPanelWrapper from './PcPanelWrapper';
import QuickButtons, { QuickButtonsRef } from './QuickButtons';
import ToolPanel from './ToolPanel';
import NoticeBar from './NoticeBar';
import Load from '../components/PageLoading';
import { IToolButton, RenderStatus, IComponent } from './type';
import { hideHeader } from '../../../../src/utils/urlParams';

export interface LayoutProps {
  appContext?: AppContextType;
  style?: React.CSSProperties;
  className?: string;
}

export interface IAppRef {
  setRender: (layoutKey: string, status: string) => void;
}

const Layout: React.FC<LayoutProps> = ({
  appContext = {} as AppContextType,
  style = {},
  className,
}) => {
  const { renderConfig: config, query = {} } = appContext || {};
  const [renderStatus, setRenderStatus] = useState({
    appBottom: null,
  });
  const { getRemoteComponent } = useContext(LocalProvider);
  const appRef = useRef<IAppRef>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const messageWrapperRef = useRef<HTMLDivElement | undefined>();
  const optionalRef = useRef<IOptionalRef>(null);
  const inputRef = useRef<IInputWrapperRef>(null);
  const quickButtonsRef = useRef<QuickButtonsRef>(null);
  const toolPanelRef = useRef<HTMLDivElement>(null);
  const panelWrapperRef = useRef<IPanelWrapperRef>(null);
  const ghostRef = useRef<GhostRefs>(null);
  const [formConfig, setFormConfig] = useState<IComponent>();
  // Click outside the area to hide the panel
  const wrapper = useClickOutside(
    () => {
      panelWrapperRef.current?.toggle(false);
    },
    (() => {
      return 'click';
    })(),
  );

  useImperativeHandle(
    appRef,
    () => {
      return {
        setRender: (layoutKey, status) => {
          setRenderStatus({
            ...renderStatus,
            [layoutKey]: status,
          });
        },
      };
    },
    [renderStatus],
  );

  useEffect(() => {
    config?.toolPanel?.map((item: IToolButton) => {
      if (item?.icon && typeof item.icon !== 'string')
        getRemoteComponent(item.icon);
    });
  }, [config]);

  useEffect(() => {
    appContext.$refs.$app = appRef;
    appContext.$refs.$optional = optionalRef; //Text box area
    appContext.$refs.$quickButtons = quickButtonsRef; //Shortcut button area
    appContext.$refs.$input = inputRef; //Input example
    appContext.$refs.$toolPanel = toolPanelRef;
    appContext.$refs.$usePanel = panelWrapperRef; //Expand the tool panel area & emoji panel area
    appContext.$refs.$scroller = messageWrapperRef; //Message list area
    appContext.$refs.$ghost = ghostRef;
  }, []);

  const FixDivStyles = useMemo<React.CSSProperties>(
    () => ({
      position: 'fixed',
      top: query.isPc && config?.navbar && !hideHeader ? 38 : 0,
      background: 'transparent',
      width: '100%',
      zIndex: 2,
    }),
    [query.isPc, config?.navbar],
  );

  const inputSendAction = useCallback(() => {
    //Solve the scenario that emjio sends a close message to be closed under PC
    if (query?.isPc) {
      panelWrapperRef.current?.toggle(false);
    }
  }, [query.isPc]);

  if (appContext.appStatus === Lifecycle.APP_ON_ERROR) {
    return <ErrorBlock fullPage status="default" />;
  }

  return appContext.appLoading ? (
    <Load />
  ) : (
    <>
      {appContext?.watchOfflineForm?.renderReactForm ? (
        <LazyComponent
          fallback={true}
          config={appContext?.watchOfflineForm?.formRenderData}
          extraData={appContext?.watchOfflineForm?.extraData}
        />
      ) : (
        <div
          className={prefixCls(
            {
              [`${classes.prefix}JarvisApp`]: true,
              [`${className || ''}`]: true,
            },
            appContext?.query?.isPc,
          )}
          style={style}
        >
          {/* Top area navbar */}
          {query.isPc && config?.navbar ? (
            <Navbar ref={headerRef} config={config?.navbar}></Navbar>
          ) : null}

          {/* 公告 */}
          <NoticeBar appContext={appContext}></NoticeBar>

          {/* The top footprint will not exceed NavBar */}
          {config?.headerFix && (
            <div className="FixTopDiv" style={FixDivStyles}>
              <LazyComponent config={config?.headerFix} />
            </div>
          )}
          {/* Message list */}
          <MessageWrapper
            config={config?.avatars}
            ref={messageWrapperRef as any}
          />

          {/* Bottom operation area */}
          {renderStatus?.appBottom !== RenderStatus.hidden ? (
            <div className="JarvisAppBottom" ref={wrapper}>
              {/* Quick operation */}
              {config?.quickButtons && config?.quickButtons.length ? (
                <QuickButtons
                  config={config?.quickButtons}
                  ref={quickButtonsRef}
                />
              ) : null}

              {/* Text box area  */}
              <Optional
                toolPanelList={config?.toolPanel}
                config={config?.inputExt}
                ref={optionalRef}
              >
                <InputWrapper
                  onBlur={() => {
                    appContext?.eventBus?.publish(LayoutEventBus.inputOnBlur);
                  }}
                  onFocus={() => {
                    appContext?.eventBus?.publish(LayoutEventBus.inputOnFocus);
                  }}
                  ref={inputRef}
                  onSend={inputSendAction}
                />
              </Optional>
              {/* Panel area, keyboard area, soft keyboard, expression panel tool panel */}
              {!query.isPc ? (
                <PanelWrapper
                  ref={panelWrapperRef}
                  inputRef={inputRef}
                  panels={[
                    {
                      name: 'emoji',
                      panel: (
                        <EmojiPanel
                          query={appContext.query}
                          onClick={(key: string) => {
                            inputRef.current?.input(key);
                          }}
                        />
                      ),
                    },
                    {
                      name: 'tool',
                      panel: (
                        <ToolPanel
                          config={config?.toolPanel}
                          ref={toolPanelRef}
                        />
                      ),
                    },
                  ]}
                />
              ) : (
                <PCPanelWrapper
                  ref={panelWrapperRef}
                  inputRef={inputRef}
                  panels={[
                    {
                      name: 'emoji',
                      panel: (
                        <EmojiPanel
                          query={appContext.query}
                          quickHidden={
                            !quickButtonsRef.current?.getQuickBtnsHeight()
                          }
                          onClick={(key: string) => {
                            inputRef.current?.input(key);
                          }}
                        />
                      ),
                    },
                    {
                      name: 'tool',
                      panel: (
                        <ToolPanel
                          quickHidden={!config?.quickButtons?.length}
                          config={config?.toolPanel}
                          ref={toolPanelRef as any}
                        />
                      ),
                    },
                  ]}
                />
              )}
            </div>
          ) : null}
          <Popup
            visible={appContext.renderConfig.popConfig?.visible}
            position="right"
            bodyStyle={{ width: '100vw' }}
          >
            {appContext.renderConfig.popConfig?.visible ? (
              <LazyComponent
                fallback={true}
                loadIcon={<Load />}
                config={appContext.renderConfig.popConfig?.componentConfig}
                extraData={appContext.renderConfig.popConfig?.componentData}
              />
            ) : null}
          </Popup>
          <DynamicStyles />
          <Ghost ref={ghostRef}></Ghost>
        </div>
      )}
    </>
  );
};

export default inject<{
  appContext: AppContextType;
}>(({ appContext }) => ({
  appContext,
}))(observer(Layout));
